'use client';

import { useCallback, useMemo, useSyncExternalStore } from 'react';

/**
 * Whether a CSS media query currently matches.
 *
 * For the cases Tailwind's `lg:` prefix cannot cover: not "style this differently on a phone" but
 * "render something structurally different there" — a panel that lives in the layout on a desktop
 * and inside a drawer on a phone cannot be two places in the DOM at once, and duplicating it would
 * duplicate its state along with it.
 *
 * Built on `useSyncExternalStore` rather than `useEffect` + `setState`, for two reasons. It is what
 * React wants for anything read from outside React (it renders the server snapshot through
 * hydration and swaps to the client one afterwards, with no extra render pass), and this repo's
 * ESLint forbids `react-hooks/set-state-in-effect` outright, which is the shape the naive version
 * takes.
 *
 * The server snapshot is always `false`. There is no viewport during SSR, so every query has to
 * resolve to *something*, and "no" is the safe answer: a layout that renders its desktop form and
 * then collapses is a visual correction, whereas one that renders its mobile form and then expands
 * would have mounted the drawer — and its focus trap — around content that was never in a drawer.
 */
export function useMediaQuery(query: string): boolean {
    const subscribe = useCallback(
        (onStoreChange: () => void) => {
            // `matchMedia` is missing in jsdom-less test environments and in any non-browser
            // renderer, so subscribing has to be safe to call where it does not exist.
            if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
                return () => {};
            }
            const mediaQueryList = window.matchMedia(query);
            mediaQueryList.addEventListener('change', onStoreChange);
            return () => mediaQueryList.removeEventListener('change', onStoreChange);
        },
        [query]
    );

    const getSnapshot = useCallback(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return false;
        }
        return window.matchMedia(query).matches;
    }, [query]);

    const getServerSnapshot = useMemo(() => () => false, []);

    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * The repo's `lg` breakpoint (1024px, themes.ts) expressed as "narrower than". Named rather than
 * repeated, so a component asking "am I on the mobile layout?" and a `lg:` class in the same
 * component can never disagree about where the boundary is.
 */
export const BELOW_LG_MEDIA_QUERY = '(max-width: 1023px)';

export function useIsBelowLg(): boolean {
    return useMediaQuery(BELOW_LG_MEDIA_QUERY);
}

/**
 * Whether the primary pointer is imprecise — a finger rather than a mouse.
 *
 * Deliberately not "is the screen narrow": the two answer different questions. A drag that starts
 * after 4px of movement is right for a mouse and wrong for a finger at *any* width, because on a
 * touch screen the same 4px is how a page is scrolled — so on a tablet in landscape, which is wide
 * enough to get the desktop layout, a width test would still hand the canvas a gesture that fights
 * scrolling.
 */
export const COARSE_POINTER_MEDIA_QUERY = '(pointer: coarse)';

export function useIsCoarsePointer(): boolean {
    return useMediaQuery(COARSE_POINTER_MEDIA_QUERY);
}
